import {inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {AuthSessionApiService, BiometryVendor, CheckLivenessStatus, OzCheckLivenessRequest} from '@npck/auth-frontend-api';
import {LocalizationService, REQUEST_ID_HEADER, rxSpinner, SentryService} from '@npck/libs/commons';
import {Observable, Subject} from 'rxjs';
import {LivenessServiceResponse, LivenessServiceStatus} from '../../../pages/liveness-device/liveness-device.component';
import {LivenessService} from '../../liveness.service';
import {Liveness} from '../liveness';
import {type OzLiveness, type OzLivenessCaptureComplete, type OzLivenessError} from './utils/oz-liveness.service';
import {type ScriptLoaderConfig, ScriptLoaderService} from '../utils/script-loader.service';

const SDK_LOAD_TIMEOUT_MS = 15000;
const OZ_ACTIONS = ['video_selfie_blank', 'video_selfie_eyes', 'video_selfie_smile'];
const OZ_PLUGIN_PATH = '/oz-forensics/plugin_liveness.php';
const OZ_ERROR_CODE_NO_CAMERA = 'error_no_camera';
const OZ_ERROR_CODE_CLOSED_BY_USER = 'closed_by_user';
const OZ_USER_CLOSED_ERROR_MESSAGE = 'user closed the plugin';
const OZ_UNSPECIFIED_ERROR_MESSAGE = 'an error occurred';

@Injectable()
export class OzLivenessVendorService implements Liveness, OnDestroy {

    private scriptLoader = inject(ScriptLoaderService) as ScriptLoaderService<OzLiveness>;
    private ngZone = inject(NgZone);
    private destroyed = false;

    constructor(
        private authSessionApi: AuthSessionApiService,
        private localizationService: LocalizationService,
        private sentryService: SentryService,
        private livenessService: LivenessService,
    ) {
    }

    private get sdk(): OzLiveness | undefined {
        return this.scriptLoader.value() ? window.OzLiveness : undefined;
    }

    init(): void {
        const config: ScriptLoaderConfig = {
            src: OZ_PLUGIN_PATH,
            globalName: 'OzLiveness',
            timeoutMs: SDK_LOAD_TIMEOUT_MS,
        };
        this.scriptLoader.load(config);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    startLiveness(): Observable<LivenessServiceResponse> {
        const result$ = new Subject<LivenessServiceResponse>();

        const checkSdkReady = (): void => {
            if (this.destroyed) {
                return;
            }
            if (this.sdk) {
                this.onSdkReady(result$);
            } else if (this.scriptLoader.error()) {
                this.onSdkLoadError(this.scriptLoader.error(), result$);
            } else {
                setTimeout(() => checkSdkReady(), 100);
            }
        };
        checkSdkReady();

        return result$.asObservable();
    }

    skipLiveness(): Observable<LivenessServiceResponse> {
        const result$ = new Subject<LivenessServiceResponse>();
        const request: OzCheckLivenessRequest = {vendor: BiometryVendor.OZFORENSICS, container: 'ZHVtbXk='};
        this.authSessionApi.checkLiveness(request, 'response')
            .subscribe(response => {
                result$.next({
                    status: LivenessServiceStatus.CLOSED,
                    requestId: response.headers.get(REQUEST_ID_HEADER),
                    rootStatus: response.body.status,
                });
            });
        return result$.asObservable();
    }

    private onSdkReady(result$: Subject<LivenessServiceResponse>): void {
        this.livenessService.addBreadcrumb('OzLiveness SDK ready');
        result$.next({status: LivenessServiceStatus.LIVENESS_STARTED, rootStatus: 'OZ_SDK_READY'});

        this.authSessionApi.initLiveness()
            .pipe(rxSpinner('loading'))
            .subscribe({
                next: response => {
                    if (this.destroyed) {
                        return;
                    }
                    this.openSdk(response.sessionToken, result$);
                },
                error: (error: Error) => {
                    this.livenessService.logOzError('OZ_SESSION_TOKEN_FAILED', error?.message);
                    result$.next({status: LivenessServiceStatus.LIVENESS_ERROR, rootStatus: 'OZ_SESSION_TOKEN_FAILED'});
                },
            });
    }

    private openSdk(sessionToken: string, result$: Subject<LivenessServiceResponse>): void {
        let isCaptured = false;
        let isResolved = false;

        const resolveAsCancel = (rootStatus: string, breadcrumb: string): void => {
            isResolved = true;
            this.livenessService.addBreadcrumb(breadcrumb);
            result$.next({status: LivenessServiceStatus.CANCEL, rootStatus});
        };

        this.sdk.open({
            action: OZ_ACTIONS,
            lang: this.localizationService.currentLanguage?.locale,
            session_token: sessionToken,

            on_capture_complete: (captureResult: OzLivenessCaptureComplete, container: Blob) => {
                this.ngZone.run(() => {
                    if (isResolved) {
                        return;
                    }
                    isCaptured = true;
                    isResolved = true;
                    this.livenessService.addBreadcrumb('OzLiveness capture complete', {
                        eventSessionId: captureResult.event_session_id,
                    });
                    this.submitContainer(container, result$);
                });
            },

            on_error: (error: OzLivenessError) => {
                this.ngZone.run(() => {
                    if (isCaptured) {
                        this.livenessService.logOzError('OZ_ERROR', error.message);
                        return;
                    }
                    if (isResolved) {
                        return;
                    }
                    if (error.code === OZ_ERROR_CODE_NO_CAMERA) {
                        isResolved = true;
                        this.sdk?.hide?.();
                        this.livenessService.logOzError('OZ_CAMERA_UNAVAILABLE', error.message);
                        result$.next({status: LivenessServiceStatus.CAMERA_FAILED, rootStatus: 'OZ_CAMERA_UNAVAILABLE'});
                        return;
                    }
                    if (
                        error.code === OZ_ERROR_CODE_CLOSED_BY_USER ||
                        error.message?.toLowerCase().includes(OZ_USER_CLOSED_ERROR_MESSAGE)
                    ) {
                        resolveAsCancel('OZ_CLOSED_BY_USER', 'OzLiveness closed by user');
                        return;
                    }
                    if (error.message?.trim().toLowerCase() === OZ_UNSPECIFIED_ERROR_MESSAGE && !document.hasFocus()) {
                        this.livenessService.logOzError('OZ_ERROR_WINDOW_UNFOCUSED', error.message);
                        resolveAsCancel('OZ_WINDOW_UNFOCUSED', 'OzLiveness error while window was unfocused');
                        return;
                    }
                    isResolved = true;
                    this.livenessService.logOzError('OZ_ERROR', error.message);
                    result$.next({status: LivenessServiceStatus.LIVENESS_ERROR, rootStatus: error.message || 'OZ_ERROR'});
                });
            },

            on_close: () => {
                this.ngZone.run(() => {
                    if (isCaptured || isResolved) {
                        return;
                    }
                    resolveAsCancel('OZ_CLOSED_BY_USER', 'OzLiveness closed by user');
                });
            },
        }).catch((error: Error) => {
            this.ngZone.run(() => {
                if (isCaptured || isResolved) {
                    return;
                }
                isResolved = true;
                this.livenessService.logOzError('OZ_OPEN_FAILED', error?.message);
                result$.next({status: LivenessServiceStatus.LIVENESS_ERROR, rootStatus: 'OZ_OPEN_FAILED'});
            });
        });
    }

    private submitContainer(container: Blob, result$: Subject<LivenessServiceResponse>): void {
        const reader = new FileReader();
        reader.onloadend = (): void => {
            const base64 = (reader.result as string).split(',')[1];
            const request: OzCheckLivenessRequest = {vendor: BiometryVendor.OZFORENSICS, container: base64};

            this.authSessionApi.checkLiveness(request)
                .pipe(rxSpinner('loading'))
                .subscribe({
                    next: response => {
                        result$.next({status: this.mapStatus(response.status), rootStatus: response.status});
                        if (response.status === CheckLivenessStatus.SUCCEED) {
                            result$.next({status: LivenessServiceStatus.CLOSED, rootStatus: response.status});
                        }
                    },
                    error: (error: Error) => {
                        this.livenessService.logOzError('OZ_CAPTURE_SUBMIT_FAILED', error?.message);
                        result$.next({status: LivenessServiceStatus.LIVENESS_FAILED, rootStatus: 'OZ_LIVENESS_FAILED'});
                    },
                });
        };
        reader.readAsDataURL(container);
    }

    private mapStatus(status: CheckLivenessStatus): LivenessServiceStatus {
        switch (status) {
            case CheckLivenessStatus.SUCCEED:
                return LivenessServiceStatus.SUCCEED;
            case CheckLivenessStatus.FAILED_LIVENESS:
                return LivenessServiceStatus.LIVENESS_FAILED;
            case CheckLivenessStatus.FAILED_FACE_NOT_MATCHED:
                return LivenessServiceStatus.FACE_NOT_MATCHED;
            case CheckLivenessStatus.LIMIT_PER_SESSION_EXCEEDED:
                return LivenessServiceStatus.LIMIT_PER_SESSION_EXCEEDED;
            default:
                return LivenessServiceStatus.LIVENESS_FAILED;
        }
    }

    private onSdkLoadError(error: Error, result$: Subject<LivenessServiceResponse>): void {
        this.livenessService.logOzError('SDK_LOAD_FAILED', error?.message);
        if (error?.message?.includes('not found after')) {
            result$.next({status: LivenessServiceStatus.NETWORK_PROBLEM, rootStatus: 'OZ_SDK_TIMEOUT'});
        } else {
            result$.next({status: LivenessServiceStatus.NETWORK_PROBLEM, rootStatus: 'OZ_SDK_LOAD_FAILED'});
        }
    }
}
